import { useMemo } from 'react'
import { clsx } from 'clsx'
import { Flag } from '@/shared/components/ui/Flag'
import { TransferWindowState, type GameweekStatus } from '../types'
import {
  MatchStatus,
  useGameweekMatches,
  type GameweekMatch,
  type MatchTeam,
} from '../queries'
import { DeadlineCard } from './DeadlineCard'
import styles from './MatchdayPanel.module.css'

type MatchdayPanelProps = {
  gameweekId: string | undefined
  gwNumber: number
  gwStatus: GameweekStatus
  onAction: () => void
}

// ─── Date helpers (all in America/São_Paulo / BRT) ──────────────────────────────

const BRT = 'America/Sao_Paulo'

const dayKeyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: BRT, year: 'numeric', month: '2-digit', day: '2-digit',
})
const dayLabelFmt = new Intl.DateTimeFormat('pt-BR', {
  timeZone: BRT, weekday: 'short', day: '2-digit', month: 'short',
})
const timeFmt = new Intl.DateTimeFormat('pt-BR', {
  timeZone: BRT, hour: '2-digit', minute: '2-digit',
})

function brtDayKey(iso: string): string {
  return dayKeyFmt.format(new Date(iso))
}

function brtDayLabel(iso: string): string {
  // "qui., 11 de jun." → "Qui · 11 jun"
  return dayLabelFmt
    .format(new Date(iso))
    .replace('.,', ' ·')
    .replace(/\sde\s/, ' ')
    .replace(/\.$/, '')
    .replace(/(^\w)/, (c) => c.toUpperCase())
}

function relativeDayLabel(dayKey: string): string | null {
  const todayKey = dayKeyFmt.format(new Date())
  if (dayKey === todayKey) return 'Hoje'
  const oneDay = 86_400_000
  if (dayKey === dayKeyFmt.format(new Date(Date.now() + oneDay)))  return 'Amanhã'
  if (dayKey === dayKeyFmt.format(new Date(Date.now() - oneDay)))  return 'Ontem'
  return null
}

// ─── Match-status presentation ──────────────────────────────────────────────────

function statusMeta(m: GameweekMatch): { label: string; tone: 'live' | 'ft' | 'up' | 'off' } {
  switch (m.status) {
    case MatchStatus.Live:      return { label: 'Ao vivo',   tone: 'live' }
    case MatchStatus.Halftime:  return { label: 'Intervalo', tone: 'live' }
    case MatchStatus.Finished:  return { label: 'Encerrado', tone: 'ft' }
    case MatchStatus.Postponed: return { label: 'Adiado',    tone: 'off' }
    default:                    return { label: 'A definir', tone: 'up' }
  }
}

function hasScore(m: GameweekMatch): boolean {
  return m.homeScore != null && m.awayScore != null &&
    (m.status === MatchStatus.Live || m.status === MatchStatus.Halftime || m.status === MatchStatus.Finished)
}

// ─── Sub-components ─────────────────────────────────────────────────────────────

function TeamCell({
  team, placeholder, side,
}: { team: MatchTeam | undefined; placeholder: string | undefined; side: 'home' | 'away' }) {
  const code = team?.code ?? '—'
  const name = team?.name ?? placeholder ?? 'A definir'
  const badge = <Flag code={team?.code} title={name} />

  const text = (
    <span className={clsx(styles.teamText, team?.isEliminated && styles.eliminated)}>
      <span className={styles.code}>{team ? code : '—'}</span>
      <span className={styles.full}>{name}</span>
    </span>
  )

  return (
    <div className={clsx(styles.team, side === 'home' ? styles.home : styles.away)}>
      {side === 'home' ? <>{text}{badge}</> : <>{badge}{text}</>}
    </div>
  )
}

function MatchRow({ match }: { match: GameweekMatch }) {
  const meta = statusMeta(match)
  const group = match.homeTeam?.groupCode ?? match.awayTeam?.groupCode
  const subtitle =
    meta.tone === 'up'
      ? (group ? `Grupo ${group}` : (match.isThirdPlaceMatch ? '3º lugar' : 'A definir'))
      : meta.label

  return (
    <div className={clsx(styles.match, match.status === MatchStatus.Live && styles.matchLive)}>
      <div className={styles.time}>
        <span className={styles.clock}>{timeFmt.format(new Date(match.kickoffAt))}</span>
        <span className={clsx(styles.status, styles[meta.tone])}>
          {meta.tone === 'live' && <span className={styles.liveDot} />}
          {subtitle}
        </span>
      </div>

      <TeamCell team={match.homeTeam} placeholder={match.homeTeamPlaceholder} side="home" />

      <div className={styles.center}>
        {hasScore(match) ? (
          <span className={styles.score}>
            <span>{match.homeScore}</span>
            <span className={styles.sep}>:</span>
            <span>{match.awayScore}</span>
          </span>
        ) : (
          <span className={styles.vs}>vs</span>
        )}
      </div>

      <TeamCell team={match.awayTeam} placeholder={match.awayTeamPlaceholder} side="away" />
    </div>
  )
}

// ─── Main panel ─────────────────────────────────────────────────────────────────

export function MatchdayPanel({ gameweekId, gwNumber, gwStatus, onAction }: MatchdayPanelProps) {
  const { data: matches, isLoading, isError, refetch } = useGameweekMatches(gameweekId)

  const days = useMemo(() => {
    if (!matches) return []
    const sorted = [...matches].sort((a, b) => a.kickoffAt.localeCompare(b.kickoffAt))
    const groups = new Map<string, { label: string; rel: string | null; matches: GameweekMatch[] }>()
    for (const m of sorted) {
      const key = brtDayKey(m.kickoffAt)
      const existing = groups.get(key)
      if (existing) existing.matches.push(m)
      else groups.set(key, { label: brtDayLabel(m.kickoffAt), rel: relativeDayLabel(key), matches: [m] })
    }
    return [...groups.entries()].map(([key, g]) => ({ key, ...g }))
  }, [matches])

  const isGroupStage = useMemo(
    () => !!matches?.some((m) => m.homeTeam?.groupCode || m.awayTeam?.groupCode),
    [matches],
  )

  const isOpen = gwStatus.windowState === TransferWindowState.Open
  const firstMatch = matches?.[0]
  const lastMatch  = matches && matches.length > 0 ? matches[matches.length - 1] : undefined

  return (
    <div className={styles.grid}>
      <div className={styles.main}>
        {/* Phase + window strip */}
        <div className={clsx(styles.phaseStrip, !isOpen && styles.phaseLocked)}>
          <div className={styles.phaseLeft}>
            {isGroupStage && <div className={styles.phaseEyebrow}>Fase de grupos</div>}
            <div className={styles.phaseTitle}>Rodada {gwNumber}</div>
            <div className={styles.phaseMeta}>
              <span className={clsx(styles.lockPill, isOpen ? styles.lockOpen : styles.lockClosed)}>
                {isOpen ? '🔓 Janela aberta' : '🔒 Escalação bloqueada'}
              </span>
              {matches && matches.length > 0 && (
                <span className={styles.phaseCount}>{matches.length} jogos</span>
              )}
            </div>
          </div>
          <button className={styles.phaseCta} onClick={onAction}>
            {isOpen ? 'Montar escalação →' : 'Ver escalação →'}
          </button>
        </div>

        {/* Match list */}
        {isLoading ? (
          <div className={styles.skeletonList}>
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className={styles.skeletonRow} />)}
          </div>
        ) : isError ? (
          <div className={styles.empty}>
            <p>Não foi possível carregar os jogos desta rodada.</p>
            <button className={styles.retry} onClick={() => void refetch()}>Tentar novamente</button>
          </div>
        ) : days.length === 0 ? (
          <div className={styles.empty}>
            <p>Nenhum jogo agendado para esta rodada.</p>
          </div>
        ) : (
          days.map((day) => (
            <div key={day.key} className={styles.day}>
              <div className={styles.dayHead}>
                <span className={styles.dayDate}>{day.label}</span>
                {day.rel && <span className={styles.dayRel}>{day.rel}</span>}
                <span className={styles.dayCount}>{day.matches.length} jogos</span>
              </div>
              <div className={styles.dayList}>
                {day.matches.map((m) => <MatchRow key={m.id} match={m} />)}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Rail */}
      <div className={styles.rail}>
        <DeadlineCard gwStatus={gwStatus} onAction={onAction} />

        {matches && matches.length > 0 && (
          <div className={styles.card}>
            <div className={styles.cardHead}><span className={styles.cardTitle}>Sobre a rodada</span></div>
            <div className={styles.about}>
              {isGroupStage && (
                <div className={styles.aboutRow}><span className={styles.aboutK}>Fase</span><span className={styles.aboutV}>Fase de grupos</span></div>
              )}
              <div className={styles.aboutRow}><span className={styles.aboutK}>Total de jogos</span><span className={styles.aboutV}>{matches.length}</span></div>
              {firstMatch && (
                <div className={styles.aboutRow}>
                  <span className={styles.aboutK}>Primeiro jogo</span>
                  <span className={styles.aboutV}>{brtDayLabel(firstMatch.kickoffAt)}, {timeFmt.format(new Date(firstMatch.kickoffAt))}</span>
                </div>
              )}
              {lastMatch && (
                <div className={styles.aboutRow}>
                  <span className={styles.aboutK}>Último jogo</span>
                  <span className={styles.aboutV}>{brtDayLabel(lastMatch.kickoffAt)}, {timeFmt.format(new Date(lastMatch.kickoffAt))}</span>
                </div>
              )}
              <div className={styles.aboutRow}><span className={styles.aboutK}>Fuso</span><span className={styles.aboutV}>Horário de Brasília</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
